import React, { useState } from "react";
import { Field } from "formik";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import MediaUploadPopup from "./MediaUploadPopup";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminTableBody,
  AdminMainText,
} from "../../sections/AdminDashboard/shared/adminUx";

const MediaUploadWidget = ({ errors }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  // Define fields with estimated completion times
  const fields = [
    { name: "Profile picture", key: "profilePicture", estimatedTime: "2 min", required: false },
    { name: "Cover images", key: "coverImageUpload", estimatedTime: "2 min", required: false }
  ];

  // Internal state to track completion
  const [completionState, setCompletionState] = useState({
    profilePicture: null,
    coverImageUpload: null
  });

  // Display values based on internal state
  const displayValues = {
    profilePicture: completionState.profilePicture,
    coverImageUpload: completionState.coverImageUpload
  };

  // Calculate completion percentage
  const calculateProgress = () => {
    const completedFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key] !== null;
      return hasValue;
    }).length;
    
    return Math.round((completedFields / fields.length) * 100);
  };

  // Calculate estimated time for remaining fields
  const calculateEstimatedTime = () => {
    const remainingFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key] !== null;
      return !hasValue;
    });
    
    const totalMinutes = remainingFields.reduce((total, field) => {
      const timeStr = field.estimatedTime;
      const minutes = parseInt(timeStr.replace(' min', ''));
      return total + minutes;
    }, 0);
    
    return totalMinutes > 0 ? `Estimated ${totalMinutes} min` : 'All Completed';
  };

  const progress = calculateProgress();
  const estimatedTime = calculateEstimatedTime();

  // Get file names for display
  const getFileName = (file) => {
    if (!file) return "";
    return file.name || "Uploaded";
  };

  return (
    <>
      <DashboardMainContainer className="masonry-item w-[500px] h-auto account-widget">
        <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
          <AdminPanelTitle className="text-offblack">Media Upload</AdminPanelTitle>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-lightgray rounded-full overflow-hidden">
              <div 
                className="h-full bg-freshgreen transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <AdminMetric size="micro" className="text-offblack tabular-nums">
              {progress}%
            </AdminMetric>
          </div>
        </div>
        
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              const hasValue = displayValues[field.key] && displayValues[field.key] !== null;
              const isCompleted = hasValue; // Only completed if it actually has a value
              
              return (
                <div key={field.key} className="flex items-center justify-between">
                    {progress === 100 ? (
                      <div 
                        className={`group flex items-center w-full py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2 ${index < fields.length - 1 ? '' : ''}`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <AdminTableBody emphasis className="w-56 text-offblack group-hover:text-primaryblue">
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className="text-offblack group-hover:text-primaryblue truncate">
                          {isCompleted ? getFileName(displayValues[field.key]) : `Estimated ${field.estimatedTime}`}
                        </AdminMainText>
                      </div>
                    ) : (
                    <>
                      <div 
                        className={`group flex items-center py-[4px] cursor-pointer hover:text-primaryblue rounded px-2 -mx-2 ${index < fields.length - 1 ? 'border-b border-transparent' : ''}`}
                        onClick={() => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <div className={`w-2 h-2 rounded-full mr-3 ${isCompleted ? 'bg-freshgreen' : 'bg-mediumgray'}`}></div>
                        <AdminTableBody emphasis className={`w-40 ${isCompleted ? 'text-offblack group-hover:text-primaryblue' : 'text-mediumgray group-hover:text-primaryblue'}`}>
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className={`text-offblack group-hover:text-primaryblue truncate max-w-[200px]`}>
                          {isCompleted ? getFileName(displayValues[field.key]) : ''}
                        </AdminMainText>
                      </div>
                      <AdminMetadataText className="text-mediumgray">
                        {isCompleted ? 'Completed' : 'Start'}
                      </AdminMetadataText>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </DashboardMainContainer>

      {/* Popup for editing */}
      {isPopupOpen && (
        <MediaUploadPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          errors={errors}
          initialStep={selectedFieldIndex}
          onCompletionUpdate={(fieldKey, value) => {
            setCompletionState(prev => ({
              ...prev,
              [fieldKey]: value
            }));
          }}
        />
      )}
    </>
  );
};

export default MediaUploadWidget;
